import React, { useState } from 'react';
import { User, Mail, Briefcase, Stethoscope, AlertCircle, Pill, FileText, Heart, Brain, Coffee, Moon, Droplets, Calendar, Activity, Cigarette, Syringe, Dumbbell, Apple } from 'lucide-react';

const MedicalIntakeForm = () => {
  const [formData, setFormData] = useState({
    codiceFiscale: '',
    email: '',
    professione: '',
    medicoCurante: '',
    allergie: '',
    terapiaAttuale: '',
    anamnesiFisiologica: '',
    aFamiliare: '',
    aPatRemota: '',
    aPatProssima: '',
    digestione: '',
    alvoMicraobiota: '',
    sonno: '',
    urineMinzione: '',
    mestruo: '',
    problemiUrogenitali: '',
    soggettivitaCardiovascolare: '',
    fumoDroghe: '',
    vaccini: '',
    attivitaFisica: '',
    shenPsichismo: '',
    alimentazione: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!formData.codiceFiscale || !formData.email) {
      alert('Per favore compila i campi obbligatori (Codice Fiscale e Email)');
      return;
    }
    
    console.log('Form Data Submitted:', formData);
    alert('Dati inviati con successo!');
  };

  const handleReset = () => {
    setFormData({
      codiceFiscale: '',
      email: '',
      professione: '',
      medicoCurante: '',
      allergie: '',
      terapiaAttuale: '',
      anamnesiFisiologica: '',
      aFamiliare: '',
      aPatRemota: '',
      aPatProssima: '',
      digestione: '',
      alvoMicraobiota: '',
      sonno: '',
      urineMinzione: '',
      mestruo: '',
      problemiUrogenitali: '',
      soggettivitaCardiovascolare: '',
      fumoDroghe: '',
      vaccini: '',
      attivitaFisica: '',
      shenPsichismo: '',
      alimentazione: ''
    });
  };

  const FormSection = ({ icon: Icon, title, children }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center mb-4">
        <Icon className="w-5 h-5 text-blue-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
      </div>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );

  const FormField = ({ label, name, type = "text", rows = 3, required = false, maxLength }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {rows > 1 ? (
        <textarea
          name={name}
          rows={rows}
          value={formData[name]}
          onChange={handleChange}
          maxLength={maxLength}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
          placeholder={`Inserisci ${label.toLowerCase()}`}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleChange}
          maxLength={maxLength}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder={`Inserisci ${label.toLowerCase()}`}
        />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Scheda Paziente</h1>
          <p className="text-gray-600 mb-8">Compila tutti i campi per completare la registrazione</p>
          
          {/* Informazioni Personali */}
          <FormSection icon={User} title="Informazioni Personali">
            <div className="grid md:grid-cols-2 gap-4">
              <FormField 
                label="Codice Fiscale" 
                name="codiceFiscale" 
                required={true}
                rows={1}
                maxLength={256}
              />
              <FormField 
                label="Email" 
                name="email" 
                type="email" 
                required={true}
                rows={1}
                maxLength={256}
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <FormField 
                label="Professione" 
                name="professione"
                rows={1}
                maxLength={256}
              />
              <FormField 
                label="Medico Curante" 
                name="medicoCurante"
                rows={1}
                maxLength={256}
              />
            </div>
          </FormSection>

          {/* Informazioni Mediche */}
          <FormSection icon={AlertCircle} title="Informazioni Mediche">
            <FormField 
              label="Allergie" 
              name="allergie" 
              rows={2}
            />
            <FormField 
              label="Terapia Attuale" 
              name="terapiaAttuale" 
              rows={3}
            />
          </FormSection>

          {/* Anamnesi */}
          <FormSection icon={FileText} title="Anamnesi">
            <FormField 
              label="Anamnesi Fisiologica" 
              name="anamnesiFisiologica" 
              rows={3}
            />
            <FormField 
              label="Anamnesi Familiare" 
              name="aFamiliare" 
              rows={3}
            />
            <FormField 
              label="Anamnesi Patologica Remota" 
              name="aPatRemota" 
              rows={3}
            />
            <FormField 
              label="Anamnesi Patologica Prossima" 
              name="aPatProssima" 
              rows={3}
            />
          </FormSection>

          {/* Funzioni Fisiologiche */}
          <FormSection icon={Activity} title="Funzioni Fisiologiche">
            <div className="grid md:grid-cols-2 gap-4">
              <FormField 
                label="Digestione" 
                name="digestione" 
                rows={2}
              />
              <FormField 
                label="Alvo/Microbiota" 
                name="alvoMicraobiota" 
                rows={2}
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <FormField 
                label="Sonno" 
                name="sonno" 
                rows={2}
              />
              <FormField 
                label="Urine/Minzione" 
                name="urineMinzione" 
                rows={2}
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <FormField 
                label="Mestruo" 
                name="mestruo" 
                rows={2}
              />
              <FormField 
                label="Problemi Urogenitali" 
                name="problemiUrogenitali" 
                rows={2}
              />
            </div>
          </FormSection>

          {/* Sistema Cardiovascolare e Abitudini */}
          <FormSection icon={Heart} title="Sistema Cardiovascolare e Abitudini">
            <FormField 
              label="Soggettività Cardiovascolare (palpitazioni, tachicardia, angoscia, nodo alla gola...)" 
              name="soggettivitaCardiovascolare" 
              rows={3}
            />
            <div className="grid md:grid-cols-2 gap-4">
              <FormField 
                label="Fumo/Droghe" 
                name="fumoDroghe" 
                rows={2}
              />
              <FormField 
                label="Vaccini" 
                name="vaccini" 
                rows={2}
              />
            </div>
          </FormSection>

          {/* Stile di Vita */}
          <FormSection icon={Dumbbell} title="Stile di Vita">
            <FormField 
              label="Attività Fisica" 
              name="attivitaFisica" 
              rows={2}
            />
            <FormField 
              label="Shen (Psichismo)" 
              name="shenPsichismo" 
              rows={3}
            />
            <FormField 
              label="Alimentazione" 
              name="alimentazione" 
              rows={3}
            />
          </FormSection>

          {/* Submit Buttons */}
          <div className="flex justify-end mt-8 space-x-4">
            <button
              onClick={handleReset}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancella
            </button>
            <button
              onClick={handleSubmit}
              className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
            >
              Invia Scheda
            </button>
          </div>
        </div>

        {/* Display Current Data (for development/testing) */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-700">Dati Correnti (Debug)</h2>
          <pre className="bg-gray-50 p-4 rounded-lg text-xs overflow-auto">
            {JSON.stringify(formData, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default MedicalIntakeForm;